import { Suspense, lazy } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';

import isValidProp from '@emotion/is-prop-valid';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { NatterThemeProvider } from 'common/hocs';
import { VISUAL_REFRESH, applyVisualRefreshClass, logEnabledFeatures } from 'common/utils';
import { MotionConfig } from 'framer-motion';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';
import { AuthProvider } from 'modules/authorization';
import { KeycloakProvider } from 'modules/keycloak/context';
import { createRoot } from 'react-dom/client';
import { reduxHistory } from 'reduxHistory';
import { store } from 'store';

import './components/styles/design-system.css';
import { peerConnectionRegistry } from 'webRTC/observer';

import { initDatadogLogs } from './datadog-logs';
import { initDatadogRum } from './datadog-rum';
import './index.css';
import i18n from './i18n';
import { initBrowserMsw } from './mocks/initBrowserMsw';
import { PreApp } from './preApp';
import { initSentry } from './sentry';

const DeviceTestPage = import.meta.env.DEV
  ? lazy(() => import('pages/DeviceTest/DeviceTestPage').then((m) => ({ default: m.DeviceTestPage })))
  : null;

// Must run before any call joins. The Agora SDK is already loaded by now (store imports
// agora-rtc-react), so its load-time capability probes are missed, which is intended; it resolves
// `new RTCPeerConnection(...)` at call time, so every call connection goes through the wrapper.
peerConnectionRegistry.installIfConfigured(window._env_.VITE_APP_OBSERVER_API_URL);
initSentry();
initDatadogRum();
initDatadogLogs();
initBrowserMsw();
logEnabledFeatures();

// Resolve the permissions host here, at boot, rather than leaving it to the first request. `getUrl`
// is only ever reached from the per-request `baseURL` thunk, which sits inside `_axiosBaseQuery`'s
// `try` — a throw there is swallowed into an ordinary query error, so the app would render normally
// and, because the permission layer fails closed, silently deny every action. Failing here turns a
// missing `VITE_APP_PERMISSIONS_API_URL` into a loud blank page instead.
configService.getUrl(UrlNames.PermissionsApi);

/** Hand the resolved flag to the two `font-family` declarations CSS owns (HNS-260). */
applyVisualRefreshClass(VISUAL_REFRESH);

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
  <I18nextProvider i18n={i18n}>
    <Router history={reduxHistory}>
      <StyledEngineProvider injectFirst>
        <MotionConfig reducedMotion="user" isValidProp={isValidProp}>
          <NatterThemeProvider>
            <CssBaseline />
            <Provider store={store}>
              <Switch>
                {DeviceTestPage && (
                  <Route exact path="/dev/device-test">
                    <Suspense fallback={null}>
                      <DeviceTestPage />
                    </Suspense>
                  </Route>
                )}
                <Route>
                  <AuthProvider>
                    <KeycloakProvider>
                      <PreApp />
                    </KeycloakProvider>
                  </AuthProvider>
                </Route>
              </Switch>
            </Provider>
          </NatterThemeProvider>
        </MotionConfig>
      </StyledEngineProvider>
    </Router>
  </I18nextProvider>
);
